/* ===================================================================
   Lightweight analytics event bus (onboarding redesign BUILD-SPEC
   2026-07-07, §11 metrics).

   Generalizes the Phase-5 trackEntryModelExposure emitter (config/entryModel)
   into ONE track(event, props) helper that every funnel milestone flows
   through. It emits a window CustomEvent 'rhody:analytics' (the shared bus a
   real forwarder can listen for) and logs in dev. There is a SINGLE seam here
   to wire a real product-analytics sink into later.

   NO PII. Callers pass only anonymous tenant ids (client_id, a UUID), item
   ids, counts, and timestamps -- never names, emails, phone numbers, or review
   text. Keep it that way.

   TODO(analytics): forward to a real sink (PostHog / Segment / GA4) at the
   marked spot in track(). Until then this is a no-network stub.
   =================================================================== */

export type AnalyticsProps = Record<string, unknown>;

/* The one emitter. Every event flows through here so there is a single place to
   wire the real sink into. */
export function track(event: string, props: AnalyticsProps = {}): void {
  const payload = { event, ...props, ts: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent('rhody:analytics', { detail: payload }));
  } catch {
    /* non-fatal (no window / storage blocked) */
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[analytics]', event, payload);
  }
  // TODO(analytics): forward to the real sink once wired, e.g.
  //   posthog.capture(event, payload)
  //   analytics.track(event, payload)
}

/* ---- time-to-value support (spec §11: activation timestamp vs first-seen) ----
   first-seen is stamped ONCE per device on first app boot; activation reads it
   back to compute time-to-value. Stored as a bare epoch-ms string -- no PII. */
const FIRST_SEEN_KEY = 'rhody:first_seen_ts';

export function getFirstSeen(): number | null {
  try {
    const v = localStorage.getItem(FIRST_SEEN_KEY);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/* Stamp first-seen if absent. Returns true when it was NEWLY stamped (this is
   the device's first-ever boot -- a coarse signup/first-run proxy). */
export function markFirstSeen(): boolean {
  try {
    if (localStorage.getItem(FIRST_SEEN_KEY)) return false;
    localStorage.setItem(FIRST_SEEN_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/* ---- once-per-tenant milestone de-dupe -------------------------------------
   Funnel milestones (item completed, activation) must fire ONCE per tenant, not
   on every render / mount / reload. We remember emitted milestone keys in
   localStorage, namespaced by client id. Returns true when it ACTUALLY emitted
   (first time for this tenant), so the caller can chain a follow-up (e.g.
   time_to_value on the activation edge). */
function emittedKey(clientId: string): string {
  return `rhody:analytics_emitted:${clientId}`;
}
function loadEmitted(clientId: string): Set<string> {
  try {
    const raw = localStorage.getItem(emittedKey(clientId));
    if (!raw) return new Set();
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}
function saveEmitted(clientId: string, set: Set<string>): void {
  try {
    localStorage.setItem(emittedKey(clientId), JSON.stringify([...set]));
  } catch {
    /* non-fatal */
  }
}

export function trackMilestoneOnce(
  clientId: string | undefined,
  milestone: string,
  props: AnalyticsProps = {},
): boolean {
  if (!clientId) return false;
  const set = loadEmitted(clientId);
  if (set.has(milestone)) return false;
  set.add(milestone);
  saveEmitted(clientId, set);
  track(milestone, { client_id: clientId, ...props });
  return true;
}

/* ---- app boot --------------------------------------------------------------
   Fire app_boot ONCE per page load (module-level guard) and stamp first-seen.
   On the device's first-ever boot also emit a coarse signup proxy. Safe to call
   from every mount -- app_boot still fires only once per load. */
let bootedThisLoad = false;
export function trackAppBoot(): void {
  if (bootedThisLoad) return;
  bootedThisLoad = true;
  const firstEver = markFirstSeen();
  track('app_boot', { first_seen: firstEver });
  if (firstEver) {
    // TODO(analytics): emit the TRUE "signup" from Auth on account creation;
    // this device-first-boot proxy is a stand-in until that seam is wired.
    track('signup_first_seen', {});
  }
}
